import { ProductType } from '@/shared/enums';
import dayjs from 'dayjs';
import { decryptText } from '@/shared/utils';
import { Emitter } from '@/shared/plugins/stats/emitter';
import ConsentMap from './consent-map';
import EventType from './event-type';
import ReportingAdapter from './reporting-adapter';
import { getViewParams, insertTrackingPixel } from './helpers';
import {
  getCategory,
  getChannelTitle,
  getDeviceParams,
  getDuration,
  getLanguageIso6392,
  getProfileType,
  getServiceParams,
  getTechnicalParams,
  getTitle,
  getType,
  mapByFieldName,
  normalizeQuality,
  removeEmptyParams,
} from '../stats/helpers';

export default class IpressoEmitter extends Emitter {
  constructor(opts = {}) {
    super();
    const { $services, $stats, $oneTrust, $storage, $router } = opts;
    this.oneTrust = $oneTrust;
    this.router = $router;
    this.stats = $stats;
    this.storage = $storage;
    this.categories = $services.resolve('categories');
    this.info = $services.resolve('info');
    this.liveManager = $services.resolve('liveManager');
    this.subscriber = $services.resolve('subscriber');

    this.subscriber.on('sync', this.updateUserParams, this);
    this.subscriber.on('login', this.subscriberSync, this);
    this.subscriber.on('login:auto', this.subscriberSync, this);
    this.subscriber.on('logout', this.onSubscriberLogout, this);

    this.init();
    this.isFirstPageView = true;
  }

  init() {
    const idCustomer = decryptText(this.info.ipressoConfiguration.idCustomer, process.env.VUE_APP_IPRESSO_DECRYPTION_KEY, this.info.ipressoConfiguration.iv);
    const appId = decryptText(this.info.ipressoConfiguration.appId, process.env.VUE_APP_IPRESSO_DECRYPTION_KEY, this.info.ipressoConfiguration.iv);
    const dataReportingField = this.info.diipActivityEndpoint;
    const dataAuthorizationField = this.info.reportingInfo.authorizationUrl;
    this.adapter = new ReportingAdapter({ appId, dataAuthorizationField, dataReportingField, idCustomer, subscriber: this.subscriber });
    this.defaultParams = {
      ...getDeviceParams(),
      ...getServiceParams(),
    };
    this.updateUserParams();
    this.adapter.subscriberSync(this.subscriber);
  }

  subscriberSync() {
    insertTrackingPixel(this.subscriber);
    this.adapter.subscriberSync(this.subscriber);
  }

  onSubscriberLogout() {
    this.storage.ipressoContact = {};
    this.subscriberSync();
  }

  pageView() {
    const params = getViewParams(this.router, this.categories);
    if (params) {
      this.event(EventType.SHOW_APP_VIEW, params);
    }

    // __ipStartMonitoring call is already included in the ipresso script on load
    if (this.isFirstPageView) {
      this.isFirstPageView = false;
      return;
    }

    if (window.__ipStartMonitoring) {
      window.__ipStartMonitoring(false);
    }
  }

  event(apiKey, params = {}, opts = {}) {
    if (!this.oneTrust.areConsentsAccepted(ConsentMap[apiKey])) {
      return;
    }

    this.adapter.action(apiKey, removeEmptyParams({ ...this.defaultParams, ...params }), opts);
  }

  adEvent(eventType, adParams, product) {
    this.event(eventType, {
      ...this.getVideoMetadata(product),
      ...this.getAdsMetadata(adParams),
      ...getTechnicalParams(product, this.stats.bitrateMonitor),
    });
  }

  eventWithoutUserProfile(apiKey, params = {}) {
    if (!this.oneTrust.areConsentsAccepted(ConsentMap[apiKey])) {
      return;
    }

    const mergedParams = removeEmptyParams({ ...this.defaultParams, ...params });
    delete mergedParams.param_user_profile_name;
    delete mergedParams.param_user_profile_id;
    delete mergedParams.param_user_profile_type;
    this.event(apiKey, mergedParams);
  }

  getAdsMetadata(adParams = {}) {
    const params = {
      param_ad_type: adParams.type,
      param_ad_position: adParams.position,
      param_ad_error: adParams.error,
    };

    removeEmptyParams(params);
    return params;
  }

  getVideoMetadata(product) {
    const { actors, countries, directors, id, genres, production, provider, rating, scriptwriters, type, year } = product;
    const episode = type === ProductType.EPISODE ? product : product.episode;
    const isClip = type === ProductType.CLIP;

    const params = {
      param_age_limit: rating,
      param_episode: episode,
      param_id: id,
      param_imdb_rating: '', // @todo
      param_prodction_year: year,
      param_category: getCategory(product),
      param_channel: getChannelTitle(product, this.liveManager),
      param_length: getDuration(product),
      param_title: getTitle(product),
      param_cast: mapByFieldName(actors, 'name'),
      param_countries: mapByFieldName(countries, 'externalUid'),
      param_directors: mapByFieldName(directors, 'name'),
      param_genres: mapByFieldName(genres, 'key'),
      param_screenwriters: mapByFieldName(scriptwriters, 'name'),
      param_season: product.season ? product.season.number : '',
      param_type: getType(product),
      ...(production && { param_producers: [production.key] }),
      ...(provider && { param_studio: [provider.key] }),
    };

    if (episode) {
      params.param_episode = episode.episode;
      params.param_season = episode.season?.number;
      params.param_season_id = episode.season?.id;
      params.param_series = episode.season?.serial?.id;
    } else if (isClip) {
      const { parentProduct, relatedEpisodeDto, relatedSeasonDto, relatedSeriesDto } = product;
      params.param_episode = relatedEpisodeDto?.number || (parentProduct?.type === ProductType.EPISODE ? parentProduct.number : undefined);
      params.param_season = relatedSeasonDto?.number || (parentProduct?.type === ProductType.SEASON ? parentProduct.number : parentProduct?.season?.number);
      params.param_season_id = relatedSeasonDto?.id || (parentProduct?.type === ProductType.SEASON ? parentProduct.id : parentProduct?.season?.id);
      params.param_series = relatedSeriesDto?.id || (parentProduct?.type === ProductType.SERIAL ? parentProduct.id : parentProduct?.serial?.id);
    }

    return params;
  }

  updateUserParams() {
    if (this.subscriber.ipressoMonitoringPixel) {
      insertTrackingPixel(this.subscriber);
    }

    if (this.subscriber.isLogged) {
      const profile = this.subscriber.profile;

      Object.assign(this.defaultParams, {
        param_subscriber_id: this.subscriber.id,
        param_user_profile_name: profile.name,
        param_user_profile_id: profile.id,
        date_of_birth: this.subscriber.dateOfBirth && dayjs(this.subscriber.dateOfBirth).format('YYYY-MM-DD'),
        sex: this.subscriber.gender?.toLowerCase(),
        param_user_profile_type: getProfileType(profile),
      });
    } else {
      delete this.defaultParams.param_subscriber_id;
      delete this.defaultParams.param_user_profile_name;
      delete this.defaultParams.param_user_profile_id;
      delete this.defaultParams.param_user_profile_type;
    }
  }

  startPlaying({ product }) {
    this.event(EventType.START_PLAYING, {
      ...this.getVideoMetadata(product),
      ...getTechnicalParams(product, this.stats.bitrateMonitor),
    });
  }

  pausePlaying({ product, playerCurrentTime }) {
    this.event(EventType.PAUSE_PLAYING, {
      ...this.getVideoMetadata(product),
      ...getTechnicalParams(product, this.stats.bitrateMonitor),
      param_time_of_pause: playerCurrentTime,
    });
  }

  stopPlaying({ product, playerCurrentTime }) {
    this.event(EventType.STOP_PLAYING, {
      ...this.getVideoMetadata(product),
      ...getTechnicalParams(product, this.stats.bitrateMonitor),
      param_time_of_stop: playerCurrentTime,
    });
  }

  clickModal({ params }) {
    this.event(EventType.POPUP_CLICK, {
      param_popup_name: params.modalName,
      popup_page_url: params.url,
      param_button_name: params.clickedButtonName,
    });
  }

  closeModal({ params }) {
    this.event(EventType.POPUP_CLOSE, {
      param_popup_name: params.modalName,
      popup_page_url: params.url,
    });
  }

  showModal({ params }) {
    this.event(EventType.POPUP_SHOW, {
      param_popup_name: params.modalName,
      popup_page_url: params.url,
    });
  }

  bufferingWhilePlaying({ adParams, bufferingDuration, product, playerCurrentTime }) {
    this.event(EventType.BUFFERING_WHILE_PLAYING, {
      ...this.getVideoMetadata(product),
      ...this.getAdsMetadata(adParams),
      ...getTechnicalParams(product, this.stats.bitrateMonitor),
      param_time_of_buffering: playerCurrentTime,
      param_buffering_duration: bufferingDuration,
    });
  }

  errorWhilePlaying({ product, playerCurrentTime, errorCode }) {
    this.event(EventType.ERROR_WHILE_PLAYING, {
      ...this.getVideoMetadata(product),
      ...getTechnicalParams(product, this.stats.bitrateMonitor),
      param_time_of_error: playerCurrentTime,
      param_error_code: errorCode,
    });
  }

  changePlayingSettings({ quality, product }) {
    this.event(EventType.CHANGE_PLAYING_SETTINGS, {
      ...this.getVideoMetadata(product),
      ...getTechnicalParams(product, this.stats.bitrateMonitor),
      param_stream_quality: normalizeQuality(quality),
    });
  }

  adStart({ adParams, product }) {
    this.adEvent(EventType.AD_STARTED, adParams, product);
  }

  adClick({ adParams, product }) {
    this.adEvent(EventType.AD_CLICKED, adParams, product);
  }

  adFinish({ adParams, product }) {
    this.adEvent(EventType.AD_FINISHED, adParams, product);
  }

  addToFavorites({ product }) {
    this.event(EventType.ADD_TO_FAVORITES, this.getVideoMetadata(product));
  }

  login({ isAutoLogin }) {
    this.event(EventType.LOGIN, { param_autologin: isAutoLogin });
  }

  logout({ profile, id }) {
    const opts = { saveContact: true };

    this.event(EventType.LOGOUT, {
      param_subscriber_id: id,
      param_user_profile_name: profile.name,
      param_user_profile_id: profile.id,
      param_user_profile_type: getProfileType(profile),
    }, opts);
  }

  changeProfile({ previousProfile, currentProfile }) {
    this.eventWithoutUserProfile(EventType.CHANGE_PROFILE, {
      param_previous_user_profile_name: previousProfile.name,
      param_previous_user_profile_id: previousProfile.id,
      param_previous_user_profile_type: getProfileType(previousProfile),
      param_current_user_profile_name: currentProfile.name,
      param_current_user_profile_id: currentProfile.id,
      param_current_user_profile_type: getProfileType(currentProfile),
    });
  }

  changeLanguage({ language }) {
    this.event(EventType.APP_LANGUAGE_CHANGE, {
      param_language: getLanguageIso6392(language),
    });
  }

  showSignUpForm() {
    this.event(EventType.SHOW_SIGN_UP_FORM);
  }
}
